/*
**  rui-button-group-option.js
**
**  rui-button-group-option component class
*/
import { LitElement, html, css } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ruiCommonStyles } from '../common/rui-common-styles.js';
export class RuiButtonGroupOption extends LitElement {
    static get is() {
        return 'rui-button-group-option';
    }
    static get properties() {
        return {
            value: { type: String },
            iconType: { type: String },
            isDisabled: { type: Object },
            _isActive: { type: Boolean, attribute: false },
            _isVertical: { type: Boolean, attribute: false }
        };
    }
    constructor() {
        super();
        this.value = null;
        this.iconType = '';
        this.isDisabled = false;
        this._isActive = false;
        this._isVertical = false;
        this._hasCustomIcon = false;
    }
    connectedCallback() {
        super.connectedCallback();
        let optionInitEvent = new CustomEvent('rui-button-group-option-init', {
            detail: this.value,
            bubbles: true,
            composed: true
        });
        this.dispatchEvent(optionInitEvent);
    }
    ;
    click(e) {
        e.preventDefault();
        e.stopPropagation();
        if (!this.isDisabled) {
            let optionClickEvent = new CustomEvent('rui-button-group-option-click', {
                detail: this.value,
                bubbles: true,
                composed: true
            });
            this.dispatchEvent(optionClickEvent);
        }
    }
    render() {
        let iconContext = 'secondary-btn';
        const classes = {
            ['rui-button-group-option']: true,
            ['rui-button-group-option-active']: this._isActive,
            ['rui-button-group-option-vertical']: this._isVertical
        };
        if (this.isDisabled) {
            iconContext = 'disabled-btn';
        }
        else if (this._isActive) {
            iconContext = 'primary-btn';
        }
        if (this.querySelector(`*[slot='custom-icon']`)) {
            this._hasCustomIcon = true;
        }
        return html `
            <button class="${classMap(classes)}" ?disabled=${this.isDisabled} aria-disabled="${this.isDisabled}" type="button" @click="${this.click}">
                ${this.iconType ? html `<span class="sr-only">${this.iconType}</span>` : ''}
                ${this.iconType || this._hasCustomIcon ? html `<rui-icon iconType="${this.iconType}" context="${iconContext}" class="rui-btn-icon" aria-hidden="true"><slot name="custom-icon"></slot></rui-icon>` : ''}
                <slot></slot>
            </button>
        `;
    }
    static get styles() {
        return [
            ruiCommonStyles,
            css `
            :host(:last-child) .rui-button-group-option-vertical {
                margin: 0;
            }

            .rui-button-group-option {            
                background-color: var(--rui-btn-secondary-bg, #fff);
                border: none;
                border-radius: var(--rui-btn-border-radius, 16px);
                color: var(--rui-btn-secondary-fg, #085ce5);
                cursor: pointer;
                font-family: var(--rui-font-family-chrome, Gellix, "Noto Sans", Roboto, Arial, -apple-system, system-ui, sans-serif);
                font-size: var(--rui-btn-font-size-md, 13px);
                font-weight: var(--rui-btn-font-weight, 500);
                line-height: var(--rui-btn-line-height-md, 1.42857143);
                min-height: 100%;
                margin: 0;
                padding: 5px 11px;
                position: relative;
                transition: background 0.25s;
                user-select: none;
                z-index: auto;
            }

            @media (prefers-reduced-motion: reduce) {
                .rui-button-group-option {
                    transition: none;
                }
            }

            .rui-button-group-option-vertical {
                display: block;
                margin: 0 0 2px 0;
                width: 100%;
            }
            
            .rui-button-group-option:hover {
                background-color: var(--rui-btn-secondary-bg-hover, #e8edff);
            }
            
            .rui-button-group-option:focus {
                background-color: var(--rui-btn-secondary-bg-hover, #e8edff);
                outline: var(--rui-outline-width, 1px) var(--rui-outline-style, solid) var(--rui-btn-secondary-border, #aec8f5);
                outline-offset: var(--rui-outline-offset-btn, 0);
                z-index: 1;
            }
            
            .rui-button-group-option:active {
                outline: var(--rui-outline-width, 1px) var(--rui-outline-style, solid) var(--rui-btn-secondary-border, #aec8f5);
                outline-offset: var(--rui-outline-offset-btn, 0);
            }
            
            .rui-button-group-option[disabled] {
                cursor: not-allowed;
            }
            
            .rui-button-group-option-active,
            .rui-button-group-option-active:focus,
            .rui-button-group-option-active:active {
                background-color: var(--rui-btn-primary-bg, #085ce5);
                color: var(--rui-btn-primary-fg, #fff);
            }

            .rui-button-group-option-active:hover {
                background-color: var(--rui-btn-primary-bg-hover, #0648b1);
            }
            
            .rui-button-group-option:disabled {
                background-color: transparent;
                border-color: transparent;
                color: var(--rui-btn-secondary-fg-disabled, #999);
                outline: none;
            }

            .rui-button-group-option-active:disabled {
                background-color: var(--rui-btn-secondary-bg-disabled-active, #FF0000);
            }

            .rui-btn-icon {
                flex: 0 0 auto;
                margin: 2px 2px 0 0;
                bottom: -1px;
                position: relative;
            }

            @media print {
                .rui-button-group-option {
                    border-color: #000000;
                    color: #000000;
                }

                .rui-button-group-option-active,
                .rui-button-group-option-active:hover,
                .rui-button-group-option-active:focus,
                .rui-button-group-option-active:active {
                    background-color: #ccc;
                    color: #000000;
                }
            }
        `
        ];
    }
}
RuiButtonGroupOption.shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };
customElements.get(RuiButtonGroupOption.is) || customElements.define(RuiButtonGroupOption.is, RuiButtonGroupOption);
